import * as React from 'react';

import { cn } from '@/lib/cn';

/**
 * Rule of materials: `surface` (default) is the opaque card; `sunken` is the
 * tray that holds cards; `ink` is the one hero card of a page; `glass` is for
 * floating layers only. The redesigned kit (kit-next.css) paints them; in the
 * current kit they fall back to plain token classes.
 */
export type CardMaterial = 'surface' | 'sunken' | 'ink' | 'glass';

const MATERIAL_CLASS: Record<CardMaterial, string> = {
  surface: 'bg-surface text-ink shadow-e1',
  sunken: 'bg-surface-sunken text-ink',
  ink: 'bg-ink text-surface [&_[data-slot=card-title]]:text-surface [&_[data-slot=card-description]]:text-surface/70',
  glass: 'bg-surface/80 text-ink shadow-e3 backdrop-blur',
};

export function Card({
  className,
  material = 'surface',
  ...props
}: React.ComponentProps<'div'> & { material?: CardMaterial }) {
  return (
    <div
      data-slot="card"
      data-material={material}
      className={cn('rounded-card border border-line', MATERIAL_CLASS[material], className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1 px-5 pt-5', className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return <h3 data-slot="card-title" className={cn('text-sm font-semibold text-ink', className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p data-slot="card-description" className={cn('text-sm text-ink-2', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('p-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex items-center justify-end gap-2 border-t border-line px-5 py-3', className)} {...props} />;
}
